/**
 * 音声解析用の AudioContext を 1 つだけ生成して共有するモジュール。
 *
 * 録音中は波形アニメーション（useAudioAnalyser）と音量計測（recordingLevelMeter）が
 * 同じストリームを同時に解析する。それぞれが AudioContext を生成すると録音中に 2 つ
 * 同時に存在することになり、同時生成数に制限がある iOS Safari で生成に失敗しうる。
 * 診断対象の端末でだけ計測できない事態を避けるため、1 つを共有する。
 *
 * 停止中の AudioContext は資源をほとんど消費しないため破棄せず保持し続ける。利用側は
 * 後片付けで close せず、自分が接続したノードを切り離すだけにすること。
 */

import { reportError } from "@/lib/error-reporting/report-error";

// 再開を指示してから稼働状態を見張る上限時間。LINE 内ブラウザでは resume() が
// 失敗として返らず保留のままになるため、この時間で見切りをつける（#1767）。
const RESUME_WATCHDOG_MS = 3_000;

let sharedContext: AudioContext | null = null;
// 同じ画面で繰り返し取得されるため、報告は 1 回の画面表示につき 1 件に絞る。
let unavailableReported = false;
let watchdogTimerId: ReturnType<typeof setTimeout> | null = null;

// DOMException は Error を継承しないため、name / message を持つかで判定する。
// resume() の拒否理由は WebKit では DOMException（InvalidStateError 等）で届く。
function describeError(error: unknown, prefix: string): string {
  if (typeof error === "object" && error !== null && "name" in error) {
    const detail = error as { name?: string; message?: string };
    return `${prefix}: ${detail.name}: ${detail.message}`;
  }
  return `${prefix}: ${String(error)}`;
}

function reportUnavailable(detail: string): void {
  if (unavailableReported) return;
  unavailableReported = true;
  void reportError({
    error: new Error(
      `音声解析を開始できませんでした（${detail}）。音量表示と波形表示が動作しません。`
    ),
    source: "audio_analysis_unavailable",
  });
}

// resume() は端末側の自動再生ゲートに掴まれると失敗も成功も返さず保留になる。
// 捕捉だけでは取り逃がすため、稼働状態そのものを見張る。
function watchResumeResult(context: AudioContext): void {
  if (watchdogTimerId !== null) return;
  watchdogTimerId = setTimeout(() => {
    watchdogTimerId = null;
    if (context.state === "running") return;
    reportUnavailable(`resume が完了しませんでした: state=${context.state}`);
  }, RESUME_WATCHDOG_MS);
}

/**
 * 共有 AudioContext を返す。生成できない環境では null を返し、呼び出し側は計測や
 * 表示を諦める（録音そのものは止めない）。
 */
export function getSharedAudioContext(): AudioContext | null {
  if (!sharedContext) {
    try {
      sharedContext = new AudioContext();
    } catch (error) {
      reportUnavailable(
        describeError(error, "AudioContext を生成できませんでした")
      );
      return null;
    }
  }

  // iOS では利用者の操作を伴わないと停止状態で始まる。録音開始は操作起点のため
  // ここで再開させれば足りる。ただし再開が拒否される場合があり、放置すると
  // 未処理の Promise 失敗として通知に流れるため必ず捕まえる（#1767）。
  if (sharedContext.state === "suspended") {
    const context = sharedContext;
    try {
      const resumed: unknown = context.resume();
      // 旧 Safari の resume() はコールバック形式で Promise を返さないため確認する。
      if (resumed instanceof Promise) {
        void resumed.catch((error: unknown) => {
          reportUnavailable(describeError(error, "resume が拒否されました"));
        });
      }
    } catch (error) {
      reportUnavailable(describeError(error, "resume で例外が発生しました"));
    }
    watchResumeResult(context);
  }

  return sharedContext;
}

/**
 * 共有 AudioContext が稼働中かを返す。停止中の AudioContext は解析グラフを動かさず
 * 音量が常に 0 になるため、表示側はこの結果で案内を出し分ける。
 */
export function isSharedAudioContextRunning(): boolean {
  return sharedContext?.state === "running";
}

/** テスト専用: モジュール内に保持した AudioContext を破棄する。 */
export function resetSharedAudioContextForTest(): void {
  if (watchdogTimerId !== null) {
    clearTimeout(watchdogTimerId);
    watchdogTimerId = null;
  }
  sharedContext = null;
  unavailableReported = false;
}
